import { BusinessMetric, BusinessMetricsApiResponse, UnitEconomicsApiResponse } from '../types/businessMetrics';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class BusinessMetricsService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  async createMetric(metricData: Omit<BusinessMetric, 'id' | 'createdAt' | 'updatedAt'>): Promise<BusinessMetric> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(metricData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create business metric');
    }

    const result: BusinessMetricsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return Array.isArray(result.data) ? result.data[0] : result.data;
  }

  async getMetrics(filters?: { month?: string; metricType?: string; source?: string }): Promise<BusinessMetric[]> {
    const params = new URLSearchParams();
    if (filters?.month) params.append('month', filters.month);
    if (filters?.metricType) params.append('metricType', filters.metricType);
    if (filters?.source) params.append('source', filters.source);

    const url = `${API_BASE_URL}/api/business-metrics${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch business metrics');
    }

    const result: BusinessMetricsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  async getMetric(id: string): Promise<BusinessMetric> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch business metric');
    }

    const result: BusinessMetricsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return Array.isArray(result.data) ? result.data[0] : result.data;
  }

  async updateMetric(id: string, updateData: Partial<BusinessMetric>): Promise<BusinessMetric> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update business metric');
    }

    const result: BusinessMetricsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return Array.isArray(result.data) ? result.data[0] : result.data;
  }

  async deleteMetric(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete business metric');
    }
  }

  /**
   * Delete all metrics by source (e.g., csv_import, manual)
   */
  async deleteBySource(source: string): Promise<{ deletedCount: number }> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/bulk-delete?source=${source}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete metrics');
    }

    const result = await response.json();
    return result.data || { deletedCount: 0 };
  }

  /**
   * Delete all metrics for a specific month
   */
  async deleteByMonth(month: string): Promise<{ deletedCount: number }> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/bulk-delete?month=${month}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete metrics');
    }

    const result = await response.json();
    return result.data || { deletedCount: 0 };
  }

  /**
   * Delete all imported CSV data
   */
  async clearImportedData(): Promise<{ deletedCount: number }> {
    return this.deleteBySource('csv_import');
  }

  /**
   * Delete metrics from a specific CSV import by filename/source
   */
  async deleteByCsvSource(csvSource: string, month?: string): Promise<{ deletedCount: number }> {
    const params = new URLSearchParams({
      source: 'csv_import',
      csvSource: csvSource
    });
    if (month) params.append('month', month);

    const response = await fetch(`${API_BASE_URL}/api/business-metrics/bulk-delete?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete metrics');
    }

    const result = await response.json();
    return result.data || { deletedCount: 0 };
  }

  /**
   * Get list of unique CSV sources for a given month
   */
  async getCsvSources(month: string): Promise<Array<{ csvSource: string; csvFileName: string; count: number; importTimestamp: string }>> {
    const metrics = await this.getMetrics({ month, source: 'csv_import' });

    // Group by CSV source
    const sourcesMap = new Map<string, { csvFileName: string; count: number; importTimestamp: string }>();

    metrics.forEach(metric => {
      const source = metric.metadata?.csvSource;
      const fileName = metric.metadata?.csvFileName || source || 'Unknown CSV';
      const timestamp = metric.metadata?.importTimestamp;

      if (source) {
        if (sourcesMap.has(source)) {
          sourcesMap.get(source)!.count++;
        } else {
          sourcesMap.set(source, {
            csvFileName: fileName,
            count: 1,
            importTimestamp: timestamp || metric.createdAt
          });
        }
      }
    });

    return Array.from(sourcesMap.entries()).map(([csvSource, data]) => ({
      csvSource,
      ...data
    })).sort((a, b) => new Date(b.importTimestamp).getTime() - new Date(a.importTimestamp).getTime());
  }

  /**
   * Export metrics to CSV format
   */
  exportToCSV(metrics: BusinessMetric[]): string {
    if (metrics.length === 0) return '';

    // CSV Headers
    const headers = ['Metric Name', 'Type', 'Value', 'Unit', 'Month', 'Start Date', 'End Date', 'Source', 'Notes'];

    // CSV Rows
    const rows = metrics.map(m => [
      m.metricName,
      m.metricType,
      m.value.toString(),
      m.unit,
      m.month,
      m.dateRange.start,
      m.dateRange.end,
      m.source,
      m.metadata?.notes || ''
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
      .join('\n');

    return csvContent;
  }

  /**
   * Download CSV file
   */
  downloadCSV(metrics: BusinessMetric[], filename: string = 'business_metrics.csv'): void {
    const csvContent = this.exportToCSV(metrics);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Bulk import with merge/update option
   * If metric with same name and month exists, it will be updated instead of creating duplicate
   */
  async bulkImportWithMerge(metrics: Omit<BusinessMetric, 'id' | 'createdAt' | 'updatedAt'>[], mergeMode: 'create' | 'update' | 'merge'): Promise<{imported: number, updated: number}> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/import-csv?mode=${mergeMode}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ metrics, mergeMode })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import business metrics');
    }

    const result = await response.json();
    return result.data || { imported: 0, updated: 0 };
  }

  async bulkImportMetrics(metrics: Omit<BusinessMetric, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<BusinessMetric[]> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/import-csv`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ metrics })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import business metrics');
    }

    const result: BusinessMetricsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  async calculateUnitEconomics(month: string, cloudCosts: number, customCosts: number): Promise<any> {
    const params = new URLSearchParams({
      month,
      cloudCosts: cloudCosts.toString(),
      customCosts: customCosts.toString()
    });

    const response = await fetch(`${API_BASE_URL}/api/business-metrics/unit-economics/calculate?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to calculate unit economics');
    }

    const result: UnitEconomicsApiResponse = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  async getAvailableMonths(): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/api/business-metrics/months/available`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch available months');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      return [];
    }

    return result.data;
  }

  async getSummary(month?: string): Promise<any> {
    const params = month ? `?month=${month}` : '';

    const response = await fetch(`${API_BASE_URL}/api/business-metrics/summary/stats${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summary');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }
}

export default new BusinessMetricsService();
